import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import type { RouteObject } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import LandingPage from "./pages/LandingPage";
import GroupList from "./pages/GroupList";
import WeeklyPlanView from "./pages/WeeklyPlanView";
import ProfilePage from "./pages/ProfilePage";
import GroupSettings from "./pages/GroupSettings";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import WelcomePage from "./pages/WelcomePage";

// ---------------------------------------------------------------------------
// Routing (data router)
// ---------------------------------------------------------------------------
//
// The app uses a DATA router (`createBrowserRouter` + `RouterProvider`) rather
// than the classic `<BrowserRouter>` + `<Routes>` API, because `useBlocker`
// (the "notify group on leave" prompt in WeeklyPlanView) only works under a
// data router.
//
// `AuthProvider` must stay INSIDE the router context (ProtectedRoute uses
// useLocation/Navigate and pages use useNavigate), so it is a LAYOUT ROUTE:
// its element renders <AuthProvider><Outlet/></AuthProvider>, and every app
// route is a child of it — preserving the previous nesting exactly.

/** Layout route: provides auth context to every nested route. */
function AuthLayout() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

export const routes: RouteObject[] = [
  {
    element: <AuthLayout />,
    children: [
      // Public route — landing / login page
      { index: true, element: <LandingPage /> },

      // Public route — Willkommensseite (keine Anmeldung nötig)
      { path: "welcome", element: <WelcomePage /> },

      // Protected routes
      {
        path: "groups",
        element: (
          <ProtectedRoute>
            <GroupList />
          </ProtectedRoute>
        ),
      },
      {
        path: "groups/:id/plan",
        element: (
          <ProtectedRoute>
            <WeeklyPlanView />
          </ProtectedRoute>
        ),
      },
      {
        path: "groups/:id/plan/:week",
        element: (
          <ProtectedRoute>
            <WeeklyPlanView />
          </ProtectedRoute>
        ),
      },
      {
        path: "groups/:id/settings",
        element: (
          <ProtectedRoute>
            <GroupSettings />
          </ProtectedRoute>
        ),
      },
      {
        path: "profile",
        element: (
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        ),
      },

      // Public route — email verification
      { path: "verify-email", element: <VerifyEmailPage /> },

      // Fallback
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
